import { Observable, Subject, firstValueFrom, forkJoin, of, throwError, timer } from 'rxjs';
import { Injectable, EventEmitter, signal, type Signal } from '@angular/core';
import { ApiRequestOptions, ApiService } from './api.service';
import {
  CreativeBlueprint,
  CreativeBlueprintNode,
  ItemPermissionEntry,
  ItemPermissionsResponse,
  Project,
  ProjectSection,
  ProjectSubsection,
  SimpleCompany,
  SimpleUser,
  SimpleUserWithActivationStatus,
} from '../interfaces';
import { AuthService } from './auth.service';
import { PermissionService } from './permissions.service';
import { fetchAllPages } from '../utils/paged-fetch';
import { UserService } from './user.service';
import { catchError, retry, switchMap, tap } from 'rxjs/operators';
import { GeneralUtilsService } from './general-utils.service';
import { ProjectSectionService } from './project-section.service';
import { ProjectUserService } from './project-user.service';
import { ProjectHelperService } from './project-helper.service';

export const onLoadProject: EventEmitter<any> = new EventEmitter<any>();

/** ApiService rethrows the parsed body (carries `errorCode`) for 4xx, or the raw response (410). */
const isDefinitiveFailure = (err: unknown): boolean => {
  const { errorCode, status } = (err ?? {}) as { errorCode?: string; status?: number };
  return !!errorCode || (status !== undefined && status >= 400 && status < 500);
};

export const onProjectBasicInfoLoaded: EventEmitter<Project> = new EventEmitter<Project>();
export const onCompanyLoad: EventEmitter<any> = new EventEmitter();
export const onSectionsLoad: EventEmitter<any> = new EventEmitter();
export const onPermissionsAppliedToProject: EventEmitter<any> = new EventEmitter();
export const onLeaveFromProject: EventEmitter<any> = new EventEmitter();
export const onLoadedProposal: EventEmitter<any> = new EventEmitter<any>();

export const onItemSelected: EventEmitter<any> = new EventEmitter<any>();

@Injectable({
  providedIn: 'root',
})
export class ProjectService {
  colors: string[] = [];
  sections: ProjectSection[] = []; //possible sections
  subsections: ProjectSubsection[] = []; //possible subsections
  project: Project | null = null;
  company: SimpleCompany = {} as SimpleCompany;
  subsectionsSelected: ProjectSubsection[] = [];
  proposal: Project | null = null;
  isOnloadedProposal: boolean = false;
  parentSubSections: ProjectSubsection[] = [];
  subSectionDetails: ProjectSubsection[] = [];
  usersLoaded: boolean = false;
  projectOnloaded: boolean = false;
  private readonly itemPermissionsSignal = signal<ItemPermissionEntry | undefined>(undefined);
  /** Per-item CORE_PROJECT grant for the loaded project — populated before onLoadProject fires. */
  readonly projectItemPermissions: Signal<ItemPermissionEntry | undefined> =
    this.itemPermissionsSignal.asReadonly();
  private readonly ngUnsubscribe: Subject<void> = new Subject<void>();
  /**
   * Emits the projectId whose load failed (deleted, forbidden, unreachable). Deliberately separate
   * from `onLoadProject`: erroring that shared emitter closed it for the rest of the session.
   */
  readonly projectLoadFailed = new Subject<string>();
  constructor(
    private readonly api: ApiService,
    private readonly authService: AuthService,
    public permissionService: PermissionService,
    public userService: UserService,
    private readonly generalUtilsService: GeneralUtilsService,
    private readonly projectSectionService: ProjectSectionService,
    private readonly projectUserService: ProjectUserService,
    private readonly projectHelperService: ProjectHelperService
  ) {}

  public leaveFromProject(): void {
    this.projectOnloaded = false;
    setTimeout(() => {
      document.body.classList.remove('project-loaded');
    }, 0);
    this.usersLoaded = false;
    this.project = null;
    this.company = {} as SimpleCompany;
    this.sections = [];
    this.subsections = [];
    this.subsectionsSelected = [];
    this.proposal = null;
    this.isOnloadedProposal = false;
    this.parentSubSections = [];
    this.subSectionDetails = [];
    this.itemPermissionsSignal.set(undefined);

    this.ngUnsubscribe.next(undefined);
  }

  public emitProjectOnloaded(): void {
    onLoadProject.emit(this.project);
  }

  public syncProjectSections(): void {
    const sectionsId: Record<string, ProjectSection> = {};
    const subsectionsId: Record<string, ProjectSubsection> = {};
    this.sections.forEach((s: ProjectSection) => {
      sectionsId[s.id] = s;
      if (s.projectSubSections && Array.isArray(s.projectSubSections)) {
        s.projectSubSections.forEach((ss: ProjectSubsection) => {
          subsectionsId[ss.id] = ss;
        });
      }
      this.permissionService.prepareSectionAndSubSections(s);
    });
    if (this.project) {
      this.project.sections = this.sections.filter((s: ProjectSection) => this.generalUtilsService.isUUID(s.id));
      this.project.sectionsId = sectionsId;
      this.project.subsectionsId = subsectionsId;
    }
  }

  private handleProjectLoaded(): void {
    this.syncProjectSections();
    this.projectOnloaded = true;
    setTimeout(() => {
      document.body.classList.add('project-loaded');
    }, 0);
    onPermissionsAppliedToProject.emit();
    this.emitProjectOnloaded();
  }

  private handleSectionsLoaded(res: any, resolve: (value: any) => void): void {
    if (Array.isArray(res)) {
      const sectionsLength: number = this.sections.length;
      for (let i: number = 0; i < sectionsLength; i++) {
        const found: any = res.find((s: any) => s.section === this.sections[i].section);
        if (found) {
          found.properties = this.sections[i].properties;
          this.sections[i] = found;
        }
      }
    }
    this.projectHelperService.setSectionsWidth(this.sections);
    onSectionsLoad.emit(this.subsections);
    resolve(true);
  }

  private handleProjectInitialized(project: any, projectId: string): void {
    this.project = project;

    onProjectBasicInfoLoaded.emit(project);
    Promise.all([
      this.loadSections(projectId).then(() => this.loadSectionsAndSubsections(projectId)),
      this.loadProjectParticipants(projectId),
    ])
      .then(() => {
        this.handlePermissionsUpdate();
      })
      .catch(() => this.projectLoadFailed.next(projectId));
  }

  /**
   * Hydrates project.participants (the backend no longer serializes it) and the userService
   * cache before onLoadProject fires — components read participants synchronously after that
   * event. Never rejects: a failed fetch degrades to [] so project load is not blocked.
   */
  private loadProjectParticipants(projectId: string): Promise<void> {
    return new Promise<void>((resolve) => {
      forkJoin([
        this.projectUserService.getProjectParticipantWithUserActivationStatus(projectId).pipe(catchError(() => of([]))),
        this.projectUserService.getProjectUsers(projectId).pipe(catchError(() => of([]))),
      ]).subscribe({
        // The project-users endpoint returns partial SimpleUsers (no roles/rsvp) — fine for
        // the cache, whose consumers only read username/fullName. Both merges are gated on
        // the project still being current so a slow response can't seed the next project's
        // cache (prepareProjectInit resets userService.users per project). No error handler:
        // both sources degrade to [] above, so forkJoin can never error.
        next: ([participants, users]: [SimpleUserWithActivationStatus[], SimpleUser[]]) => {
          if (this.project?.id === projectId) {
            this.project.participants = participants ?? [];
            this.userService.users = [...this.userService.users, ...(participants ?? []), ...(users ?? [])];
          }
          resolve();
        },
      });
    });
  }

  private handlePermissionsUpdate(): void {
    forkJoin([this.authService.updateEffectivePermissions(), this.refreshProjectItemPermissions()]).subscribe({
      next: () => {
        this.handleProjectLoaded();
      },
    });
  }

  /** Fetches CORE_PROJECT per-item permissions for the project just loaded. Never errors (see
   *  ItemPermissionsService) so it never blocks handleProjectLoaded(). Blocking (via forkJoin) rather
   *  than fire-and-forget matters: it guarantees projectItemPermissions() is populated before
   *  onLoadProject fires, so no consumer's very first check reads undefined and silently falls back
   *  to global-only behavior. */
  private refreshProjectItemPermissions(): Observable<ItemPermissionsResponse> {
    const projectId = this.project?.id;
    if (!projectId) {
      this.itemPermissionsSignal.set(undefined);
      return of({});
    }
    return this.permissionService.getProjectItemPermissions([projectId]).pipe(
      tap((response: ItemPermissionsResponse) => {
        if (this.project?.id === projectId) {
          this.itemPermissionsSignal.set(response[projectId]);
        }
      })
    );
  }

  public prepareProjectInit(projectId: string): void {
    this.projectOnloaded = false;
    setTimeout(() => {
      document.body.classList.remove('project-loaded');
    }, 0);
    this.userService.users = [];
    this.getProject(projectId)
      .pipe(
        // A missing/forbidden project won't appear on retry — fail fast so the deep link resolves quickly.
        retry({ count: 3, delay: (err: unknown) => (isDefinitiveFailure(err) ? throwError(() => err) : timer(1000)) })
      )
      .subscribe({
        next: (p: any) => {
          this.handleProjectInitialized(p, projectId);
        },
        error: () => this.projectLoadFailed.next(projectId),
      });
  }

  public getProjectById(projectId: string): Observable<any> {
    return this.getProject(projectId);
  }

  public getSingleProject(projectId: string): Observable<any> {
    return this.api.httpGet(`/api/projects/${projectId}`);
  }

  /** Whether `projectId` is a drive-mode project; the loaded project answers without a request. */
  public async isDriveProject(projectId: string): Promise<boolean> {
    if (this.project?.id === projectId) return this.project.mode === 'drive';
    const loaded: { mode?: string } | null = await firstValueFrom(this.getSingleProject(projectId)).catch(() => null);
    return loaded?.mode === 'drive';
  }

  public loadSections(projectId?: string): Promise<any> {
    if (!projectId) projectId = this.project?.id;
    return new Promise((resolve: (value: any) => void, reject: (reason?: any) => void) => {
      this.loadSubsections(projectId)
        .then(() => {
          this.projectSectionService.getSections(projectId).subscribe({
            next: (res: any) => {
              this.handleSectionsLoaded(res, resolve);
            },
            error: () => {
              reject(new Error('Failed to load sections'));
            },
          });
        })
        .catch((err: any) => {
          reject(err);
        });
    });
  }

  public loadSectionsAndSubsections(projectId?: string): Promise<any> {
    return new Promise((resolve: (value: any) => void, reject: (reason?: any) => void) => {
      if (!projectId) projectId = this.project?.id;
      this.projectSectionService.getSectionsAndSubsections(projectId).subscribe({
        next: (res: any) => {
          if (Array.isArray(res)) {
            const sectionsLength: number = this.sections.length;
            for (let i: number = 0; i < sectionsLength; i++) {
              const found: any = res.find((s: any) => s.section === this.sections[i].section);
              if (found) {
                found.status = this.sections[i].status;
                found.properties = this.sections[i].properties;
                this.sections[i] = found;
              }
            }
          }

          this.projectHelperService.setSectionsWidth(this.sections);
          onSectionsLoad.emit(this.subsections);
          resolve(true);
        },
        error: () => {
          reject(new Error('Failed to load sections and subsections'));
        },
      });
    });
  }

  /**
   * Load all default subsections
   */
  public loadSubsections(projectId: string): Promise<any> {
    return new Promise((resolve: (value: any) => void, reject: (reason?: any) => void) => {
      if (this.subsections.length > 0) {
        resolve(this.subsections);
      } else {
        this.projectSectionService.getCreativeBlueprint(projectId, this.project?.company?.id!).subscribe({
          next: (res: CreativeBlueprint) => {
            const [sections, subSections] = this.getBlueprintSectionSubsections(projectId, res);
            this.sections = sections;
            this.subsections = subSections;
            this.projectHelperService.processSubsections(this.subsections);
            resolve(this.subsections);
          },
          error: (err: any) => reject(err),
        });
      }
    });
  }

  private convertBlueprintToSections(projectId: string, blueprint: CreativeBlueprint): ProjectSection[] {
    const sections: ProjectSection[] = blueprint.tree.map((node: CreativeBlueprintNode) => {
      const section: ProjectSection = {
        id: '',
        name: node.blueprintNodeName,
        phase: node.blueprintNodeKey,
        description: '',
        section: node.blueprintNodeKey,
        projectSubSections: [],
        projectId: projectId,
        orderNumber: node.ordering,
        properties: node.nodeProperties,
      };
      node.children?.forEach((child: CreativeBlueprintNode) => {
        const subsection: ProjectSubsection = {
          id: child.blueprintNodeId.toString(),
          name: child.blueprintNodeName,
          subSection: child.blueprintNodeKey,
          phase: node.blueprintNodeKey,
          description: '',
          section: node.blueprintNodeKey,
          projectSectionId: section.id,
          orderNumber: child.ordering,
        };
        section.projectSubSections.push(subsection);
      });
      section.projectSubSections.sort((a: ProjectSubsection, b: ProjectSubsection) => a.orderNumber - b.orderNumber);
      section.subsections = section.projectSubSections;
      return section;
    });
    sections.sort((a: ProjectSection, b: ProjectSection) => a.orderNumber - b.orderNumber);
    return sections;
  }

  private getBlueprintSectionSubsections(
    projectId: string,
    blueprint: CreativeBlueprint
  ): [ProjectSection[], ProjectSubsection[]] {
    const sections: ProjectSection[] = this.convertBlueprintToSections(projectId, blueprint);
    const subsections: ProjectSubsection[] = sections.flatMap((section: ProjectSection) => section.projectSubSections);
    return [sections, subsections];
  }

  /**
   * Get project details
   * @param id
   */
  public getProject(id: string, options: ApiRequestOptions = {}): Observable<any> {
    return this.api.httpGet('/api/projects/' + id + '?fetchAssetList=true', false, {}, options).pipe(
      tap((res: any) => {
        this.projectHelperService.processProjectResponse(res);
      })
    );
  }

  /**
   * Retrieve a company by ID
   *
   * @param id
   */
  public getCompany(id: string): Observable<any> {
    return this.api.httpGet('/api/companies/' + id);
  }

  /**
   * Retrieve complete list of companies
   */
  public getCompanies(): Observable<any> {
    return this.api.httpGet('/api/companies');
  }

  /**
   * Create a project
   *
   * @param project
   */
  public saveProject(project: Project): Observable<any> {
    const subject: Subject<any> = new Subject<any>();

    this.project = project;

    const projectForSaving: any = this.generalUtilsService.customCloneDeep(this.project);

    // If project has id (exists), just save it
    if (this.project.id) {
      // Generate new project data with some fields

      const projectData: any = {
        name: projectForSaving.name,
        description: projectForSaving.description,
        company: projectForSaving.company,
        videoGenre: projectForSaving.videoGenre,
        videoGenreOther: projectForSaving.videoGenreOther,
        manager: projectForSaving.manager,
        budget: projectForSaving.budget,
        startDate: projectForSaving.startDate,
        plannedEndDate: projectForSaving.plannedEndDate,
        languages: projectForSaving.languages,
        subTitleLanguages: projectForSaving.subTitleLanguages,
        dubbingLanguages: projectForSaving.dubbingLanguages,
        validationMode: projectForSaving.validationMode,
        validators: projectForSaving.validators,
        videoLength: projectForSaving.videoLength,
        currency: projectForSaving.currency,
        tags: projectForSaving.tags,
        assetList: projectForSaving.assetList,
      };

      if (projectForSaving.workspaceId) projectData.workspaceId = projectForSaving.workspaceId;

      if (projectForSaving.campaignId) projectData.campaignId = projectForSaving.campaignId;

      if (projectForSaving.milestoneId) {
        projectData.milestoneId = projectForSaving.milestoneId;
      }
      delete projectData.airDate;
      delete projectData.budget;
      delete projectData.currency;
      delete projectData.videoGenre;
      delete projectData.videoGenreOther;
      delete projectData.videoLength;
      delete projectData.subTitleLanguages;
      delete projectData.tags;
      delete projectData.dubbingLanguages;
      delete projectData.validationMode;
      delete projectData.languages;
      this.api.httpPut('/api/projects/' + this.project.id, projectData).subscribe({
        next: (data: any) => {
          subject.next(this.projectHelperService.processProjectResponse(data));
        },
        error: (err: any) => {
          subject.error(err);
        },
      });
    } else {
      // Create the Project
      delete projectForSaving.airDate;
      delete projectForSaving.budget;
      delete projectForSaving.currency;
      delete projectForSaving.videoGenre;
      delete projectForSaving.videoGenreOther;
      delete projectForSaving.videoLength;
      delete projectForSaving.subTitleLanguages;
      delete projectForSaving.tags;
      delete projectForSaving.dubbingLanguages;
      delete projectForSaving.validationMode;
      delete projectForSaving.languages;
      // User data is not part of the project payload anymore (RBAC migration); participants is
      // client-hydrated and must not ride along on the cloned object.
      delete projectForSaving.participants;
      delete projectForSaving.users;
      this.api
        .httpPost('/api/projects', projectForSaving)
        .pipe(
          switchMap((data: any) => {
            this.project = data;
            // Creating a project can change effective grants — bypass the TTL cache.
            return this.authService.updateEffectivePermissions(true).pipe(
              catchError(() => of(null)),
              tap(() => subject.next(this.projectHelperService.processProjectResponse(data)))
            );
          })
        )
        .subscribe({
          error: (err: any) => subject.error(err),
        });
    }

    return subject.asObservable();
  }

  /**
   * Retrieve all projects for this user. The endpoint pages by USER_PROJECTS_PAGE_SIZE with no
   * hasNext envelope, so every page is aggregated before emitting.
   */
  public getProjects(): Observable<any> {
    return fetchAllPages((page: number) =>
      this.api.httpGet(`/api/projects/users/${this.authService.getAuthUserName()}?page=${page}`)
    );
  }

  /** With a page argument fetches that single page (for infinite scroll); without one, aggregates every page. */
  public getArchivedProjects(page?: number): Observable<any> {
    const url = `/api/projects/users/${this.authService.getAuthUserName()}/archived-new`;
    return page
      ? this.api.httpGet(`${url}?page=${page}`)
      : fetchAllPages((p: number) => this.api.httpGet(`${url}?page=${p}`));
  }

  public getArchivedProjectsByWorkspace(workspaceId: string): Observable<any> {
    let url: string = `/api/projects/by-workspace/archived`;
    const urlParams: string[] = [];
    if (workspaceId) {
      urlParams.push(`workspaceIds=${workspaceId}`);
    }
    if (urlParams.length > 0) {
      url += '?' + urlParams.join('&');
    }
    return this.api.httpGet(url);
  }

  public getArchivedProjectsByCampaignId(campaignId: string, isArchived: boolean = true): Observable<any> {
    let url: string = `/api/projects/by-campaign`;
    const urlParams: string[] = [];
    if (campaignId) {
      urlParams.push(`campaignIds=${campaignId}`);
    }
    if (isArchived) {
      urlParams.push(`isArchived=${isArchived}`);
    }
    if (urlParams.length > 0) {
      url += '?' + urlParams.join('&');
    }
    return this.api.httpGet(url);
  }

  /**
   * Retrieve days count between two dates
   */
  public countDays(start: Date, end: Date): number {
    return this.projectHelperService.countDays(start, end);
  }

  public saveProjectInSessionStorage(project: any): void {
    this.projectHelperService.saveProjectInSessionStorage(project);
  }

  public getProjectFromSessionStorage(): any {
    return this.projectHelperService.getProjectFromSessionStorage();
  }

  public deleteOrArchiveProject(projectId: string): Observable<any> {
    return this.api.httpDelete(`/api/projects/${projectId}`);
  }

  public deleteProject(projectId: string, archive: boolean = true): Observable<any> {
    return this.api.httpDelete(`/api/projects/${projectId}?delete=${!archive}`);
  }

  public restoreArchive(projectId: string): Observable<any> {
    return this.api.httpPut(`/api/projects/${projectId}/restore`, {});
  }

  public getAllProjectProposals(projectId: any): Observable<any> {
    return this.api.httpGet(`/api/proposals/${projectId}/proposalStatus`);
  }

  public getProductionOwnerForRating(projectId: any, companyId: any): Observable<any> {
    return this.api.httpGet(`/api/ratingMail/${projectId}/${companyId}`);
  }

  public updateProjectStatus(args: any): Observable<any> {
    let query: string = `status=${args.status}`;
    if (args.completionDate) {
      query += `&completionDate=${args.completionDate}`;
    }
    return this.api.httpPut(`/api/projects/${args.projectId}/updateProjectStatus?${query}`, {});
  }
}
